import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './slider.css';
import styles from './DefaultPage.module.scss';
import {
  getFirestore,
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  where,
  doc,
  getDoc,
} from 'firebase/firestore';
import Asterisk from '../Icons/Asterisk';
import Footer from '../Footer/Footer';
import HeartLoader from '../HeartLoader/HeartLoader';
import logger from '../../utils/logger';
import {
  DEFAULT_HEADER_TRANSLATION,
  HEADER_TRANSLATIONS,
} from './defaultPageHeaderTranslations';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const contentVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const songCardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const HEADER_CYCLE_MS = 6500;
/** Enter: slide up from below + fade in. Exit: slide down + fade out. */
const HEADER_ENTER_Y = 22;
const HEADER_EXIT_Y = 16;
const HEADER_ENTER_DURATION = 0.52;
const HEADER_EXIT_DURATION = 0.44;

const DefaultPage = ({ openSongPage }) => {
  const scrollRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  /** Only skip animation when explicitly true (hook may be null before mount). */
  const useStaticWelcomeHeader = prefersReducedMotion === true;
  const [headerTranslationIndex, setHeaderTranslationIndex] = useState(0);
  const [carouselSongs, setCarouselSongs] = useState([]);
  const [error, setError] = useState(null);
  const [isComponentLoading, setIsComponentLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [sliderKey, setSliderKey] = useState(0);
  const db = getFirestore();

  const handleWheelCapture = useCallback((e) => {
    const el = scrollRef.current;
    if (!el) return;
    const isVertical = Math.abs(e.deltaY) > Math.abs(e.deltaX);
    if (!isVertical) return;
    el.scrollTop += e.deltaY;
    e.stopPropagation();
  }, []);

  const fetchRandomSongs = useCallback(async () => {
    logger.debug('Fetching random songs...');
    setLoadingProgress(0);

    try {
      setLoadingProgress(20);
      const randomValue = Math.random();
      const songsRef = collection(db, 'songs');

      let songQuery = query(
        songsRef,
        where('random', '>=', randomValue),
        orderBy('random'),
        limit(10)
      );

      setLoadingProgress(40);
      let snapshot = await getDocs(songQuery);

      if (snapshot.size < 10) {
        const fallbackQuery = query(
          songsRef,
          where('random', '<', randomValue),
          orderBy('random'),
          limit(10 - snapshot.size)
        );
        const fallbackSnapshot = await getDocs(fallbackQuery);
        snapshot = {
          docs: [...snapshot.docs, ...fallbackSnapshot.docs],
        };
      }

      setLoadingProgress(60);
      const songList = await Promise.all(
        snapshot.docs.map(async (docSnap, index) => {
          const songData = { id: docSnap.id, ...docSnap.data() };
          try {
            const userRef = doc(db, 'users', songData.userId);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              songData.user = userSnap.data();
            }
          } catch (userError) {
            logger.error(
              `Error fetching user data for ID: ${songData.userId}`,
              userError
            );
          }
          setLoadingProgress(60 + ((index + 1) / snapshot.docs.length) * 30);
          return songData;
        })
      );

      logger.debug('Fetched songs:', songList.length);
      setCarouselSongs(songList);
      setLoadingProgress(100);
    } catch (err) {
      logger.error('Error fetching random songs:', err);
      setError('Failed to load random songs. Please try again later.');
    } finally {
      setIsComponentLoading(false);
    }
  }, [db]);

  useEffect(() => {
    fetchRandomSongs();
  }, [fetchRandomSongs]);

  useEffect(() => {
    setSliderKey((prevKey) => prevKey + 1);
  }, []);

  useEffect(() => {
    if (useStaticWelcomeHeader) return undefined;
    const id = window.setInterval(() => {
      setHeaderTranslationIndex(
        (i) => (i + 1) % HEADER_TRANSLATIONS.length
      );
    }, HEADER_CYCLE_MS);
    return () => window.clearInterval(id);
  }, [useStaticWelcomeHeader]);

  const activeHeaderTranslation = useStaticWelcomeHeader
    ? DEFAULT_HEADER_TRANSLATION
    : HEADER_TRANSLATIONS[headerTranslationIndex];

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: false,
    arrows: false,
    adaptiveHeight: false,
    draggable: false,
    swipe: false,
    touchMove: false,
    cssEase: 'ease-in-out',
  };

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  if (isComponentLoading || carouselSongs.length === 0) {
    return (
      <motion.div
        className={styles.loadingContainer}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          className={styles.loadingBar}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <HeartLoader progress={loadingProgress} />
      </motion.div>
    );
  }

  return (
    <motion.div
      className={styles.root}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
    >
      <div
        ref={scrollRef}
        className={styles.scroll}
        role="region"
        aria-label="Default page content"
        onWheelCapture={handleWheelCapture}
      >
        <div className={styles.content}>
          <div className={styles.headerBar}>
            <div className={styles.headerBarContent}>
              <p className={styles.headerBarText}>About</p>
            </div>
          </div>
          <div className={styles.header}>
            <div className={styles.headerText}>
              <div
                className={styles.headerCycle}
                aria-label="Welcome message cycling through multiple languages"
                aria-live="off"
              >
                {useStaticWelcomeHeader ? (
                  <div
                    className={styles.headerCycleInner}
                    dir={DEFAULT_HEADER_TRANSLATION.dir || 'ltr'}
                    lang={DEFAULT_HEADER_TRANSLATION.bcp47}
                  >
                    <p className={styles.headerPrimary}>
                      {DEFAULT_HEADER_TRANSLATION.primary}
                    </p>
                    <p className={styles.headerSecondary}>
                      {DEFAULT_HEADER_TRANSLATION.secondary}
                    </p>
                  </div>
                ) : (
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={String(headerTranslationIndex)}
                      className={styles.headerCycleInner}
                      dir={activeHeaderTranslation.dir || 'ltr'}
                      lang={activeHeaderTranslation.bcp47}
                      initial={{ opacity: 0, y: HEADER_ENTER_Y }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{
                        opacity: 0,
                        y: HEADER_EXIT_Y,
                        transition: {
                          duration: HEADER_EXIT_DURATION,
                          ease: [0.4, 0, 0.2, 1],
                        },
                      }}
                      transition={{
                        duration: HEADER_ENTER_DURATION,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                    >
                      <p className={styles.headerPrimary}>
                        {activeHeaderTranslation.primary}
                      </p>
                      <p className={styles.headerSecondary}>
                        {activeHeaderTranslation.secondary}
                      </p>
                    </motion.div>
                  </AnimatePresence>
                )}
              </div>
            </div>
          </div>
          <div className={styles.body}>
            <motion.div
              className={styles.sliderWrap}
              variants={contentVariants}
              initial="hidden"
              animate="visible"
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Slider key={sliderKey} {...sliderSettings}>
                {carouselSongs.map((song, index) => (
                  <motion.div
                    className={styles.songCard}
                    key={song.id}
                    onClick={() => openSongPage(song.id)}
                    variants={songCardVariants}
                    initial="hidden"
                    animate="visible"
                    transition={{
                      duration: 0.4,
                      delay: 0.2 + index * 0.1,
                      ease: [0.25, 0.46, 0.45, 0.94],
                    }}
                  >
                    <div
                      className={styles.thumbnailImage}
                      style={{
                        backgroundImage: `url(${song.thumbnailURL})`,
                      }}
                    >
                      <div className={styles.avatarContainer}>
                        <img
                          src={song.user?.avatar || './Avatar.png'}
                          alt="avi"
                          className={styles.userThumbnail}
                        />
                      </div>
                    </div>
                    <div className={styles.songInfo}>
                      <span className={styles.songName}>{song.songName}</span>
                      <span className={styles.artist}>{song.songArtist}</span>
                    </div>
                  </motion.div>
                ))}
              </Slider>
            </motion.div>
            <motion.div
              className={styles.howItWorksWrap}
              variants={contentVariants}
              initial="hidden"
              animate="visible"
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className={styles.howItWorks}>
                <p className={styles.howItWorksTitle}>How It Works</p>
                <div className={styles.steps}>
                  <div className={styles.step}>
                    <div className={styles.stepTitle}>
                      <Asterisk />
                      <h3>Step 1</h3>
                    </div>
                    <p>
                      Find a song you like on YouTube.
                      <br />
                      <br />
                      <i>
                        If you're looking for inspiration check out these
                        resources: <br />
                      </i>
                      <a
                        href="https://lazyrecords.app/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        lazyrecords.app
                      </a>
                      <br />
                      <a
                        href="https://digthis.club/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        digthis.club
                      </a>
                    </p>
                  </div>
                  <div className={styles.step}>
                    <div className={styles.stepTitle}>
                      <Asterisk />
                      <h3>Step 2</h3>
                    </div>
                    <p>
                      Copy the url of the youtube video.
                      <br />
                      <br />
                      <i>
                        You can use full youtube urls, shortened urls, and even
                        urls from&nbsp;
                      </i>
                      <a
                        href="https://music.youtube.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Youtube Music
                      </a>
                      .
                    </p>
                  </div>
                  <div className={styles.step}>
                    <div className={styles.stepTitle}>
                      <Asterisk />
                      <h3>Step 3</h3>
                    </div>
                    <p>
                      Paste the url here and share it with your buds.
                      <br />
                      <br />
                      <i>
                        Also, if you feel like it, you're welcome to wax poetic
                        about what makes the song special. In fact, it's
                        encouraged.
                      </i>
                    </p>
                  </div>
                  <div className={styles.step}>
                    <div className={styles.stepTitle}>
                      <Asterisk />
                      <h3>Step 4</h3>
                    </div>
                    <p>
                      Listen to other songs your buds have shared.
                      <br />
                      <br />
                      <i>
                        Revel in the exchange! Find your new favorite song! Make
                        new friends! Embrace the dialectic!
                      </i>
                    </p>
                  </div>
                  <div className={styles.step}>
                    <div className={styles.stepTitle}>
                      <Asterisk />
                      <h3>Step 5</h3>
                    </div>
                    <p>
                      Repeat steps 1-4 every day <strong>forever</strong>.
                      <br />
                      <br />
                      <i>
                        Just kidding. Post whenever you feel like it, live your
                        life, go outside. You don't even have to post at all. If
                        you just want to lurk and listen that's beautiful too.
                      </i>
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
            <Footer />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default DefaultPage;
